import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
// lazyWithRetry is a drop-in for React.lazy that reloads once past a stale
// chunk after a deploy. Aliased to `lazy` so the route defs below read normally.
import { lazyWithRetry as lazy } from './lib/lazyWithRetry';
import { AppErrorFallback } from './components/AppErrorFallback';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';

// ---------------------------------------------------------------------------
// EAGER (initial chunk) — public marketing, login, and the route guards/chrome
// needed to match the first paint. New visitors hit these without a chunk
// fetch. Keep this list small; everything heavy is lazy below.
// ---------------------------------------------------------------------------
import { AppLayout } from './components/AppLayout';
import { PasswordRecoveryRedirect } from './components/PasswordRecoveryRedirect';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TrialGate } from './components/TrialGate';
import { GamificationToast } from './components/GamificationToast';
import { GamificationMoment } from './components/GamificationMoment';
import { CourseDetail } from './pages/CourseDetail';
import { Login } from './pages/Login';
import { MarketingLayout } from './components/MarketingLayout';
import { LandingPage } from './pages/LandingPage';
import { PublicCatalog } from './pages/PublicCatalog';
import { FreePapers } from './pages/FreePapers';
import { IebPage } from './pages/IebPage';
import { AuthCallback } from './pages/AuthCallback';
import { UtilityCatalog } from './pages/UtilityCatalog';
import { UtilityPage } from './pages/UtilityPage';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { NotFound } from './pages/NotFound';
import { ComingSoon } from './pages/ComingSoon';

// Contexts / providers / runtime helpers (needed before any route renders).
import { CourseProvider } from './context/CourseContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { GamificationProvider } from './context/GamificationContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { ScopeProvider } from './context/ScopeContext';
import { ScopeUrlSync } from './components/ScopeUrlSync';
import { getReservedHost } from './lib/tenant';
import { UserRole } from './types';

// ---------------------------------------------------------------------------
// LAZY (split into route chunks) — authenticated/instructor/admin/hub surfaces
// plus the heavy editor + lesson-viewer trees. These pull in framer-motion,
// tiptap, dnd-kit, the slide renderers and the Create/AI tooling, none of which
// belong in the first-paint download. Loaded on demand behind <Suspense>.
// ---------------------------------------------------------------------------
const WelcomePage = lazy(() => import('./pages/WelcomePage').then((m) => ({ default: m.WelcomePage })));
const CourseCatalog = lazy(() => import('./pages/CourseCatalog').then((m) => ({ default: m.CourseCatalog })));
const Account = lazy(() => import('./pages/Account').then((m) => ({ default: m.Account })));
const ChangePassword = lazy(() => import('./pages/ChangePassword').then((m) => ({ default: m.ChangePassword })));
const RedeemPass = lazy(() => import('./pages/RedeemPass').then((m) => ({ default: m.RedeemPass })));
const SchoolDashboard = lazy(() => import('./pages/SchoolDashboard').then((m) => ({ default: m.SchoolDashboard })));
const StudentGradebook = lazy(() => import('./pages/StudentGradebook').then((m) => ({ default: m.StudentGradebook })));
const Certificate = lazy(() => import('./pages/Certificate').then((m) => ({ default: m.Certificate })));
const Checkout = lazy(() => import('./pages/Checkout').then((m) => ({ default: m.Checkout })));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback').then((m) => ({ default: m.PaymentCallback })));
const BundleDetail = lazy(() => import('./pages/BundleDetail').then((m) => ({ default: m.BundleDetail })));

// Full-screen lesson viewer (slide renderers + theme worlds + framer-motion).
const LessonViewer = lazy(() => import('./pages/LessonViewer').then((m) => ({ default: m.LessonViewer })));
const ExperienceRoute = lazy(() => import('./pages/ExperienceRoute').then((m) => ({ default: m.ExperienceRoute })));

// Instructor surfaces.
const InstructorDashboard = lazy(() => import('./pages/InstructorDashboard').then((m) => ({ default: m.InstructorDashboard })));
const InstructorSubmissions = lazy(() => import('./pages/instructor/InstructorSubmissions').then((m) => ({ default: m.InstructorSubmissions })));
const InstructorReports = lazy(() => import('./pages/instructor/InstructorReports').then((m) => ({ default: m.InstructorReports })));
const InstructorCourses = lazy(() => import('./pages/instructor/InstructorCourses').then((m) => ({ default: m.InstructorCourses })));
const InstructorStudents = lazy(() => import('./pages/instructor/InstructorStudents').then((m) => ({ default: m.InstructorStudents })));

// Admin surfaces.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
const AdminCourses = lazy(() => import('./pages/admin/AdminCourses').then((m) => ({ default: m.AdminCourses })));
const AdminBundles = lazy(() => import('./pages/admin/AdminBundles').then((m) => ({ default: m.AdminBundles })));
const AdminSchools = lazy(() => import('./pages/admin/AdminSchools').then((m) => ({ default: m.AdminSchools })));
const AdminSchoolDetail = lazy(() => import('./pages/admin/AdminSchoolDetail').then((m) => ({ default: m.AdminSchoolDetail })));
const AdminStudents = lazy(() => import('./pages/admin/AdminStudents').then((m) => ({ default: m.AdminStudents })));
const AdminTransactions = lazy(() => import('./pages/admin/AdminTransactions').then((m) => ({ default: m.AdminTransactions })));
const SettingsLayout = lazy(() => import('./pages/admin/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })));
const IdentityPanel = lazy(() => import('./pages/admin/settings/IdentityPanel').then((m) => ({ default: m.IdentityPanel })));
const AppearancePanel = lazy(() => import('./pages/admin/settings/AppearancePanel').then((m) => ({ default: m.AppearancePanel })));
const LoginPanel = lazy(() => import('./pages/admin/settings/LoginPanel').then((m) => ({ default: m.LoginPanel })));
const MarketingPanel = lazy(() => import('./pages/admin/settings/MarketingPanel').then((m) => ({ default: m.MarketingPanel })));
const CoursesPanel = lazy(() => import('./pages/admin/settings/CoursesPanel').then((m) => ({ default: m.CoursesPanel })));
const CreditsPanel = lazy(() => import('./pages/admin/settings/CreditsPanel').then((m) => ({ default: m.CreditsPanel })));
const PaymentsPanel = lazy(() => import('./pages/admin/settings/PaymentsPanel').then((m) => ({ default: m.PaymentsPanel })));
const ExportPanel = lazy(() => import('./pages/admin/settings/ExportPanel').then((m) => ({ default: m.ExportPanel })));
const UsersPanel = lazy(() => import('./pages/admin/settings/UsersPanel').then((m) => ({ default: m.UsersPanel })));
const AdminReports = lazy(() => import('./pages/admin/AdminReports').then((m) => ({ default: m.AdminReports })));
const AdminSubmissions = lazy(() => import('./pages/admin/AdminSubmissions').then((m) => ({ default: m.AdminSubmissions })));
const TrainingMatrix = lazy(() => import('./pages/admin/TrainingMatrix').then((m) => ({ default: m.TrainingMatrix })));
const ActivityTimeline = lazy(() => import('./pages/admin/ActivityTimeline').then((m) => ({ default: m.ActivityTimeline })));
const AdminStudentDetail = lazy(() => import('./pages/admin/AdminStudentDetail').then((m) => ({ default: m.AdminStudentDetail })));
const AdminCourseResponses = lazy(() => import('./pages/admin/AdminCourseResponses').then((m) => ({ default: m.AdminCourseResponses })));
const CreditsCallback = lazy(() => import('./pages/admin/CreditsCallback').then((m) => ({ default: m.CreditsCallback })));

// Staff shell (shared by instructor/admin/hub) + hub surfaces.
const StaffLayout = lazy(() => import('./components/staff/StaffLayout').then((m) => ({ default: m.StaffLayout })));
const HubLayout = lazy(() => import('./pages/hub/HubLayout').then((m) => ({ default: m.HubLayout })));
const HubDashboard = lazy(() => import('./pages/hub/HubDashboard').then((m) => ({ default: m.HubDashboard })));
const TenantManager = lazy(() => import('./pages/hub/TenantManager').then((m) => ({ default: m.TenantManager })));
const HubAiCredits = lazy(() => import('./pages/hub/HubAiCredits').then((m) => ({ default: m.HubAiCredits })));
const HubCourses = lazy(() => import('./pages/hub/HubCourses').then((m) => ({ default: m.HubCourses })));

// Course editor + Create tooling (heaviest: tiptap, dnd-kit, scenario-builder, ai).
const CourseEditor = lazy(() => import('./pages/CourseEditor').then((m) => ({ default: m.CourseEditor })));
const CourseBuilder = lazy(() => import('./components/course-builder/CourseBuilder').then((m) => ({ default: m.CourseBuilder })));
const SketchWorkspace = lazy(() => import('./pages/instructor/SketchWorkspace').then((m) => ({ default: m.SketchWorkspace })));
const CreateWalkthrough = lazy(() => import('./pages/instructor/CreateWalkthrough').then((m) => ({ default: m.CreateWalkthrough })));
const EmbedCheck = lazy(() => import('./pages/instructor/EmbedCheck').then((m) => ({ default: m.EmbedCheck })));

// Dev-only scenario slide preview harness.
const SlidePreview = lazy(() => import('./pages/dev/SlidePreview').then((m) => ({ default: m.SlidePreview })));

// Dev-only Kula Create Studio (composition) preview harness — fake app shell,
// no DB/auth/AI (see pages/dev/CreatePreview.tsx + VITE_CREATE_STUB).
const CreatePreview = lazy(() => import('./pages/dev/CreatePreview').then((m) => ({ default: m.CreatePreview })));
// Dev-only marketing landing preview — renders MarketingLayout + LandingPage
// against a fake tenant so a theme can be built without writing it into a real
// tenant's branding (see pages/dev/MarketingPreview.tsx).
const MarketingPreview = lazy(() => import('./pages/dev/MarketingPreview').then((m) => ({ default: m.MarketingPreview })));

// Dev-only floating menu — gated to DEV builds below so it never ships to prod.
const DevMenu = lazy(() => import('./components/DevMenu').then((m) => ({ default: m.DevMenu })));

// Self-serve signup app (start.kulalearn.com) — separate route tree + brand.
const StartApp = lazy(() => import('./pages/start/StartApp').then((m) => ({ default: m.StartApp })));

/** Full-screen spinner shown while a lazy route chunk loads. */
const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
  </div>
);

/** Route tree — reads runtime feature flags from TenantContext. */
const AppRoutes: React.FC = () => {
  const { tenant, features, isLoading } = useTenant();
  // Public marketing routes are gated by the tenant-owned publish toggle,
  // not a feature flag. See docs/hub-tenant-split-plan.md.
  const isPublished = tenant?.isPublished ?? false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      {/* Routes password-recovery links to /change-password no matter where
          they land — see the component for why this must be global. */}
      <PasswordRecoveryRedirect />
      {/* Hands ?curriculum= on in-app navigations to the scope provider, which
          sits above the router and can only read the URL the page arrived on. */}
      <ScopeUrlSync />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Dev-only scenario slide preview harness (no auth/data). */}
        {import.meta.env.DEV && (
          <Route path="/dev/slide-preview" element={<SlidePreview />} />
        )}
        {import.meta.env.DEV && (
          <Route path="/dev/create-preview" element={<CreatePreview />} />
        )}
        {import.meta.env.DEV && (
          // Splat: MarketingPreview nests its own <Routes> so MarketingLayout
          // has an <Outlet/> to render the landing page into.
          <Route path="/dev/marketing-preview/*" element={<MarketingPreview />} />
        )}
        {/* ===================== */}
        {/* PUBLIC MARKETING SITE */}
        {/* "Marketing site" = landing (/) + catalog (/courses) only.
            Gated by features.sell_courses and tenant.isPublished.
            Course previews at /course/:id are a separate concern below. */}
        {/* ===================== */}
        {!features.sell_courses ? (
          <>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/courses" element={<Navigate to="/login" replace />} />
          </>
        ) : isPublished ? (
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/courses" element={<PublicCatalog />} />
            {/* The announce bar's destination — the free NSC papers, on a page
                rather than a filtered catalogue. Registered for every
                published tenant but bespoke to the revise theme, which the
                page itself enforces by redirecting anyone else to /courses. */}
            <Route path="/free" element={<FreePapers />} />
            {/* The board strip's destination — the IEB range on its own page.
                Same registration-vs-bespoke split as /free above. */}
            <Route path="/ieb" element={<IebPage />} />
            {features.utilityPages && (
              <>
                <Route path="/learn-free" element={<UtilityCatalog />} />
                <Route path="/learn-free/:slug" element={<UtilityPage />} />
              </>
            )}
          </Route>
        ) : (
          <>
            <Route path="/" element={<ComingSoon />} />
            <Route path="/courses" element={<ComingSoon />} />
          </>
        )}

        {/* Course preview — ALWAYS live, regardless of sell_courses flag.
            Renders the same component as `/enroll/:id` (CourseDetail), which
            adapts its CTA and data source to whether a viewer is signed in.
            Uses MarketingLayout when the marketing site is live, otherwise
            renders bare. */}
        {features.sell_courses && isPublished ? (
          <Route element={<MarketingLayout />}>
            <Route path="/course/:id" element={<CourseDetail />} />
            {features.bundles && (
              <Route path="/bundle/:bundleId" element={<BundleDetail />} />
            )}
          </Route>
        ) : (
          <>
            <Route path="/course/:id" element={<CourseDetail />} />
            {features.bundles && (
              <Route path="/bundle/:bundleId" element={<BundleDetail />} />
            )}
          </>
        )}

        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Legal pages (always accessible, any tenant) */}
        <Route element={<MarketingLayout />}>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Route>

        {/* Auth Pages (outside any layout) */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login />} />

        {/* School pass redemption — deliberately OUTSIDE ProtectedRoute: the
            link travels on WhatsApp, so a signed-out learner must land on an
            explainer with sign-in/sign-up CTAs (which return here after
            auth), never a bare login bounce. The page adapts to auth state;
            it can't live under AppLayout, which returns null signed-out. */}
        {features.school_passes && <Route path="/redeem" element={<RedeemPass />} />}

        {/* ===================== */}
        {/* AUTHENTICATED ROUTES */}
        {/* ===================== */}
        <Route element={<ProtectedRoute />}>
          <Route element={<TrialGate />}>
          {/* Student chrome — top nav + footer. Staff chrome lives in
              StaffLayout as a sibling, not a descendant, of AppLayout. */}
          <Route element={<AppLayout />}>
            {/* Authenticated student home. `/` is reserved for the public
                face (ComingSoon when unpublished, LandingPage when published),
                so the portal lives at `/browse`. Admins/instructors redirect
                to their own roots via ProtectedRoute. */}
            <Route path="/browse" element={<WelcomePage />} />
            {/* Authenticated course catalog — /courses is reserved for the
                public marketing catalog (gated by sell_courses + isPublished),
                so the in-app catalog lives at /catalog. */}
            <Route path="/catalog" element={<CourseCatalog />} />
            {/* Account */}
            <Route path="/account" element={<Account />} />
            <Route path="/change-password" element={<ChangePassword />} />
            {/* School contact's dashboard — their learners only; shows an
                explainer to anyone who isn't a listed contact. (/redeem lives
                at the top level so signed-out learners reach its explainer.) */}
            {features.school_passes && <Route path="/school" element={<SchoolDashboard />} />}
            {/* Course detail for enrolled or enrolling users */}
            <Route path="/enroll/:id" element={<CourseDetail />} />
            {/* Bundle detail lives with the public previews above, not here:
                like /course/:id it is ALWAYS live and adapts its CTA to whether
                a viewer is signed in, so a visitor can reach a bundle from the
                marketing catalogue. Registering it here as well would shadow
                that route and bounce anonymous visitors to login. */}
            {/* Payment routes */}
            {features.sell_courses && (
              <>
                <Route path="/checkout/:courseId" element={<Checkout />} />
                {features.bundles && (
                  <Route path="/checkout/bundle/:bundleId" element={<Checkout />} />
                )}
                <Route path="/payment/callback" element={<PaymentCallback />} />
              </>
            )}

            {/* Student-only routes */}
            <Route element={<ProtectedRoute allowedRoles={[UserRole.STUDENT]} />}>
              <Route path="/certificate/:courseId" element={<Certificate />} />
            </Route>

            {/* Student gradebook */}
            {features.studentGradebook && (
              <Route element={<ProtectedRoute allowedRoles={[UserRole.STUDENT]} />}>
                <Route path="/gradebook" element={<StudentGradebook />} />
              </Route>
            )}
          </Route>

          {/* Instructor routes — share staff shell with admins, no AppLayout. */}
          <Route element={<ProtectedRoute allowedRoles={[UserRole.INSTRUCTOR, UserRole.ADMIN]} />}>
            <Route path="/instructor" element={<StaffLayout />}>
              <Route index element={<InstructorDashboard />} />
              <Route path="courses" element={<InstructorCourses />} />
              <Route path="students" element={<InstructorStudents />} />
              <Route path="submissions" element={<InstructorSubmissions />} />
              <Route path="reports" element={<InstructorReports />} />
              <Route path="embed-check" element={<EmbedCheck />} />
              <Route path="courses/:courseId/responses" element={<AdminCourseResponses />} />
            </Route>
          </Route>

          {/* Admin routes — staff shell only, no AppLayout. */}
          <Route element={<ProtectedRoute allowedRoles={[UserRole.ADMIN]} />}>
            <Route path="/admin" element={<StaffLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="courses" element={<AdminCourses />} />
              {features.bundles && <Route path="bundles" element={<AdminBundles />} />}
              {features.school_passes && <Route path="schools" element={<AdminSchools />} />}
              {features.school_passes && <Route path="schools/:schoolId" element={<AdminSchoolDetail />} />}
              <Route path="courses/:courseId/responses" element={<AdminCourseResponses />} />
              <Route path="students" element={<AdminStudents />} />
              <Route path="transactions" element={<AdminTransactions />} />
              {features.adminReporting && (
                <>
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="reports/student/:studentId" element={<AdminStudentDetail />} />
                  <Route path="submissions" element={<AdminSubmissions />} />
                  <Route path="training-matrix" element={<TrainingMatrix />} />
                  <Route path="timeline" element={<ActivityTimeline />} />
                </>
              )}
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/admin/settings/users" replace />} />
                <Route path="users" element={<UsersPanel />} />
                <Route path="identity" element={<IdentityPanel />} />
                <Route path="appearance" element={<AppearancePanel />} />
                <Route path="login" element={<LoginPanel />} />
                {features.sell_courses && (
                  <Route path="marketing" element={<MarketingPanel />} />
                )}
                <Route path="courses" element={<CoursesPanel />} />
                <Route path="credits" element={<CreditsPanel />} />
                <Route path="payments" element={<PaymentsPanel />} />
                <Route path="export" element={<ExportPanel />} />
              </Route>
              {/* Legacy URLs → redirect to new settings IA */}
              <Route path="marketing" element={<Navigate to="/admin/settings/marketing" replace />} />
              <Route path="tenant" element={<Navigate to="/admin/settings" replace />} />
            </Route>

            {/* Paystack credit top-up callback — standalone (no staff chrome while verifying). */}
            <Route path="/admin/credits/callback" element={<CreditsCallback />} />

            {/* Hub (Super Admin) Routes — share the staff shell with /admin
                so the sidebar is consistent across cross-tenant surfaces. */}
            <Route path="/hub" element={<StaffLayout />}>
              <Route element={<HubLayout />}>
                <Route index element={<HubDashboard />} />
                <Route path="tenants" element={<TenantManager />} />
                <Route path="courses" element={<HubCourses />} />
                <Route path="ai-credits" element={<HubAiCredits />} />
              </Route>
            </Route>
          </Route>

          {/* Full Screen Pages (Still Protected) */}
          <Route path="/learn/:courseId/lesson/:lessonId" element={<LessonViewer />} />
          {/* Phase 9 §9f — standalone /experience/:slug. Enrollment enforced
              inside the page; auth gated by the surrounding ProtectedRoute. */}
          <Route path="/experience/:slug" element={<ExperienceRoute />} />

          {/* Instructor Editor - New Course Builder */}
          <Route element={<ProtectedRoute allowedRoles={[UserRole.INSTRUCTOR, UserRole.ADMIN]} />}>
            <Route path="/instructor/edit/:courseId" element={<CourseBuilder />} />
            <Route path="/instructor/edit-legacy/:id" element={<CourseEditor />} />
            <Route path="/instructor/sketch/:courseId" element={<SketchWorkspace />} />
            <Route path="/instructor/create/:courseId/:lessonId/edit/:groupId" element={<CreateWalkthrough mode="edit" />} />
            <Route path="/instructor/create/:courseId/:lessonId/:slotId" element={<CreateWalkthrough />} />
          </Route>
          </Route>{/* end TrialGate */}
        </Route>

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {/* Dev-only floating menu — lazily loaded and DEV-gated so it (and its
          service-role admin client) is fully dropped from production bundles. */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <DevMenu />
        </Suspense>
      )}
      {/* Phase G: flash overlay used by useRoomTransition to punctuate
          room-to-room navigation (Workbench ↔ Designer ↔ Create). */}
      <div id="kb-flash" />
    </BrowserRouter>
  );
};

const App: React.FC = () => {
  // `start.kulalearn.com` is a RESERVED, non-tenant host serving the self-serve
  // signup flow. It must short-circuit BEFORE TenantProvider mounts — otherwise
  // getTenantSlug() resolves 'start' as a tenant and falls back to thinkshow,
  // firing a needless tenant fetch. StartApp owns its own router and brand.
  if (getReservedHost() === 'start') {
    return (
      <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
        <PwaUpdatePrompt />
        <Suspense fallback={<RouteFallback />}>
          <StartApp />
        </Suspense>
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
    <PwaUpdatePrompt />
    <TenantProvider>
    <AuthProvider>
      <ThemeProvider>
      {/* Above CourseProvider: the catalogue scope narrows the course list every
          student surface reads, so it has to resolve before that list is built. */}
      <ScopeProvider>
      <CourseProvider>
      <GamificationProvider>
        <AppRoutes />
        <GamificationToast />
        <GamificationMoment />
      </GamificationProvider>
      </CourseProvider>
      </ScopeProvider>
      </ThemeProvider>
    </AuthProvider>
    </TenantProvider>
    <Analytics />
    </Sentry.ErrorBoundary>
  );
};

export default App;
